import { z } from 'zod';

/**
 * 模型能力表：接口校验、冻结上界计算与公开文档读取同一份数据（需求 §1.3 / §12.1）。
 * 依据火山方舟 / BytePlus Seedance 2.0 官方限制整理，见 docs/research/上游API与竞品调研.md。
 */
export const GENERATION_MODES = ['text_to_video', 'first_frame', 'first_last_frame', 'reference'] as const;
export type GenerationMode = (typeof GENERATION_MODES)[number];

export const INPUT_ROLES = ['first_frame', 'last_frame', 'reference_image', 'reference_video', 'reference_audio'] as const;
export type InputRole = (typeof INPUT_ROLES)[number];

export const ASSET_KINDS = ['image', 'video', 'audio'] as const;
export type AssetKind = (typeof ASSET_KINDS)[number];

export const CapabilitiesSchema = z.object({
  modes: z.array(z.enum(GENERATION_MODES)).min(1),
  resolutions: z.array(z.string()).min(1),
  ratios: z.array(z.string()).min(1),
  /** 分辨率 × 比例 → 输出像素，用于 token 上界估算 */
  pixels: z.record(z.string(), z.record(z.string(), z.tuple([z.number().int(), z.number().int()]))),
  duration: z.object({ min: z.number().int(), max: z.number().int(), allow_auto: z.boolean() }),
  fps: z.number().int(),
  prompt_max_chars: z.number().int(),
  inputs: z.object({
    image: z.object({
      max_count: z.number().int(),
      max_bytes: z.number().int(),
      min_side: z.number().int(),
      max_side: z.number().int(),
      min_aspect: z.number(),
      max_aspect: z.number(),
      mimes: z.array(z.string()),
    }),
    video: z.object({
      max_count: z.number().int(),
      max_bytes: z.number().int(),
      min_seconds: z.number(),
      max_seconds: z.number(),
      max_total_seconds: z.number(),
      mimes: z.array(z.string()),
    }),
    audio: z.object({
      max_count: z.number().int(),
      max_bytes: z.number().int(),
      max_total_seconds: z.number(),
      mimes: z.array(z.string()),
      requires_visual_input: z.boolean(),
    }),
  }),
  /** 透传给上游的命名空间参数：布尔 / 整数 / 枚举（字符串数组 = 允许的取值） */
  provider_options: z.record(z.string(), z.union([z.enum(['boolean', 'integer']), z.array(z.string()).min(1)])),
  /** 首帧/首尾帧模式只允许 ratio=adaptive（Seedance 2.5） */
  frame_ratio_adaptive_only: z.boolean().optional(),
});
export type Capabilities = z.infer<typeof CapabilitiesSchema>;

/** 计价规则：按每百万输出 token 定价，区分输入是否含视频（官方计价维度）。 */
export const PriceRulesSchema = z.object({
  unit: z.literal('per_million_tokens'),
  /** 单价（微元 / 百万 token），key 为 "default" 或分辨率；value 区分是否含视频输入 */
  prices: z.record(z.string(), z.object({ no_video_input: z.string(), with_video_input: z.string() })),
  /** 结算取整：向上取整到该步长（微元），10000 = 0.01 元 */
  rounding_step_micros: z.number().int().positive(),
  /** 最低用量（含视频输入时官方有最低 token），0 表示无 */
  min_tokens_with_video_input: z.number().int().nonnegative(),
});
export type PriceRules = z.infer<typeof PriceRulesSchema>;

export const PublicModelSchema = z.object({
  id: z.string(),
  object: z.literal('model'),
  display_name: z.string(),
  description: z.string(),
  version: z.string(),
  capabilities: CapabilitiesSchema,
  pricing: z.object({
    currency: z.enum(['CNY', 'USD']),
    unit: z.string(),
    rules: PriceRulesSchema,
    effective_from: z.string(),
  }),
  /** 平台为该模型配置的默认兜底链：请求带 allow_fallback=true 时按顺序使用（docs/12 §2） */
  default_fallbacks: z.array(z.string()),
});
export type PublicModel = z.infer<typeof PublicModelSchema>;

/**
 * Seedance 2.0 标准版的默认能力表（种子数据与文档示例共用）。
 * 像素表只用来算冻结上界：取方舟官方输出尺寸（480p 16:9 实测 864×496，Mini 4 秒 usage = 97 × 864 × 496 / 1024 = 40594）
 * 与旧表中面积较大者（只有 720p 21:9 保留旧的 1472×640），宁可多冻、不让结算超出冻结。
 */
export const SEEDANCE_20_CAPABILITIES: Capabilities = {
  modes: ['text_to_video', 'first_frame', 'first_last_frame', 'reference'],
  resolutions: ['480p', '720p', '1080p'],
  ratios: ['16:9', '4:3', '1:1', '3:4', '9:16', '21:9', 'adaptive'],
  pixels: {
    '480p': { '16:9': [864, 496], '4:3': [752, 560], '1:1': [640, 640], '3:4': [560, 752], '9:16': [496, 864], '21:9': [992, 432] },
    '720p': { '16:9': [1280, 720], '4:3': [1112, 834], '1:1': [960, 960], '3:4': [834, 1112], '9:16': [720, 1280], '21:9': [1472, 640] },
    '1080p': { '16:9': [1920, 1080], '4:3': [1664, 1248], '1:1': [1440, 1440], '3:4': [1248, 1664], '9:16': [1080, 1920], '21:9': [2206, 946] },
  },
  duration: { min: 4, max: 15, allow_auto: true },
  fps: 24,
  prompt_max_chars: 2000,
  inputs: {
    image: {
      max_count: 9,
      max_bytes: 30 * 1024 * 1024,
      min_side: 300,
      max_side: 6000,
      min_aspect: 0.4,
      max_aspect: 2.5,
      mimes: ['image/jpeg', 'image/png', 'image/webp', 'image/bmp', 'image/tiff', 'image/gif', 'image/heic', 'image/heif'],
    },
    video: {
      max_count: 3,
      max_bytes: 200 * 1024 * 1024,
      min_seconds: 2,
      max_seconds: 15,
      max_total_seconds: 15,
      mimes: ['video/mp4', 'video/quicktime'],
    },
    audio: {
      max_count: 3,
      max_bytes: 15 * 1024 * 1024,
      max_total_seconds: 15,
      mimes: ['audio/wav', 'audio/x-wav', 'audio/mpeg'],
      requires_visual_input: true,
    },
  },
  provider_options: { seed: 'integer', camera_fixed: 'boolean', watermark: 'boolean', generate_audio: 'boolean' },
};

/**
 * Seedance 2.5（doubao-seedance-2-5-260628）能力表，依据火山方舟《Doubao Seedance 2.5 教程》：
 * 单次 4–30 秒；全模态参考上限 30 图 + 10 视频 + 10 音频，音/视频各自总时长 ≤ 30 秒；可单独用音频参考；
 * 首帧/首尾帧任务的 ratio 只能是 adaptive（frame_ratio_adaptive_only）；像素表与 2.0 不同（480p 16:9 为 854×480）。
 */
export const SEEDANCE_25_CAPABILITIES: Capabilities = {
  modes: ['text_to_video', 'first_frame', 'first_last_frame', 'reference'],
  resolutions: ['480p', '720p', '1080p'],
  ratios: ['16:9', '4:3', '1:1', '3:4', '9:16', '21:9', 'adaptive'],
  pixels: {
    '480p': { '16:9': [854, 480], '4:3': [752, 560], '1:1': [640, 640], '3:4': [560, 752], '9:16': [480, 854], '21:9': [992, 432] },
    '720p': { '16:9': [1280, 720], '4:3': [1112, 834], '1:1': [960, 960], '3:4': [834, 1112], '9:16': [720, 1280], '21:9': [1470, 630] },
    '1080p': { '16:9': [1920, 1080], '4:3': [1664, 1248], '1:1': [1440, 1440], '3:4': [1248, 1664], '9:16': [1080, 1920], '21:9': [2206, 946] },
  },
  duration: { min: 4, max: 30, allow_auto: true },
  fps: 24,
  prompt_max_chars: 4000,
  frame_ratio_adaptive_only: true,
  inputs: {
    image: { ...SEEDANCE_20_CAPABILITIES.inputs.image, max_count: 30 },
    video: { ...SEEDANCE_20_CAPABILITIES.inputs.video, max_count: 10, min_seconds: 2, max_seconds: 30, max_total_seconds: 30 },
    audio: { ...SEEDANCE_20_CAPABILITIES.inputs.audio, max_count: 10, max_total_seconds: 30, requires_visual_input: false },
  },
  provider_options: {
    seed: 'integer',
    watermark: 'boolean',
    generate_audio: 'boolean',
    return_last_frame: 'boolean',
    // 全模态参考的子任务类型：显式指定 edit / extend 时上游前置校验，参数不合要求会同步报错而不是生成后失败
    omni_reference_task_type: ['auto', 'reference', 'edit', 'extend'],
  },
};
